import { useState } from 'react';
import { FileText, Printer, RotateCcw, Download, Loader2, Stamp } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Card } from '../ui/card';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const PRODUCTS = ['Silica Sand Dry', 'Silica Sand', 'Silica Sand Wet'];
const TRANSPORT_MODES = ['By Lorry', 'By Road', 'By Rail', 'By Air', 'By Sea'];

const INITIAL_FORM = {
  client_name: '',
  client_address: '',
  client_gstin: '',
  client_state: '',
  product: 'Silica Sand Dry',
  invoice_number: '',
  kgs: '',
  rate: '',
  date: new Date().toISOString().slice(0, 10),
  po_number: '',
  vehicle_number: '',
  transport_mode: 'By Lorry',
  reverse_charge: 'N',
  include_stamp: false,
};

export default function InvoiceGenerator() {
  const [form, setForm] = useState(INITIAL_FORM);
  const [generating, setGenerating] = useState(false);
  const [generatedInvoice, setGeneratedInvoice] = useState(null);

  const handleChange = (name, value) => {
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const baseAmount = (parseFloat(form.kgs) || 0) * (parseFloat(form.rate) || 0);
  const gstAmount = baseAmount * 0.05;
  const totalAmount = baseAmount + gstAmount;

  const handleGenerate = async () => {
    if (!form.client_name.trim()) { toast.error('Please enter client name'); return; }
    if (!form.invoice_number.trim()) { toast.error('Please enter invoice number'); return; }
    if (!form.kgs || parseFloat(form.kgs) <= 0) { toast.error('Please enter valid kgs'); return; }
    if (!form.rate || parseFloat(form.rate) <= 0) { toast.error('Please enter valid rate'); return; }

    setGenerating(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/api/invoice/generate-standalone`, {
        invoice_number: form.invoice_number.trim(),
        client_name: form.client_name.trim(),
        client_address: form.client_address.trim(),
        client_gstin: form.client_gstin.trim(),
        client_state: form.client_state.trim(),
        product: form.product,
        kgs: parseFloat(form.kgs),
        rate: parseFloat(form.rate),
        date: form.date,
        po_number: form.po_number,
        vehicle_number: form.vehicle_number,
        transport_mode: form.transport_mode,
        reverse_charge: form.reverse_charge,
        include_stamp: form.include_stamp,
      }, { headers: { Authorization: `Bearer ${token}` } });

      setGeneratedInvoice(res.data);
      toast.success(`Invoice ${res.data.invoice_number} generated`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate invoice');
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = () => {
    if (!generatedInvoice?.invoice_pdf_base64) return;
    const byteChars = atob(generatedInvoice.invoice_pdf_base64);
    const byteArray = new Uint8Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) byteArray[i] = byteChars.charCodeAt(i);
    const blob = new Blob([byteArray], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Invoice_${generatedInvoice.invoice_number.replace(/\//g, '-')}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handlePrint = () => {
    if (!generatedInvoice?.invoice_pdf_base64) return;
    const byteChars = atob(generatedInvoice.invoice_pdf_base64);
    const byteArray = new Uint8Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) byteArray[i] = byteChars.charCodeAt(i);
    const blob = new Blob([byteArray], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const w = window.open(url);
    if (w) w.onload = () => w.print();
  };

  const handleReset = () => {
    setForm(INITIAL_FORM);
    setGeneratedInvoice(null);
  };

  return (
    <div data-testid="invoice-generator">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-slate-800" style={{ fontFamily: "'Manrope', sans-serif" }}>
          Standalone Invoice Generator
        </h2>
        <Button variant="outline" size="sm" onClick={handleReset} className="gap-1.5 text-slate-600 border-slate-300" data-testid="invoice-gen-reset">
          <RotateCcw className="w-3.5 h-3.5" /> Reset
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Left — Client & Invoice Details */}
        <Card className="p-5 border-0 shadow-sm bg-white rounded-2xl lg:col-span-2">
          {/* Client Details Section */}
          <h3 className="text-sm font-bold text-slate-700 mb-3 uppercase tracking-wide">Client Details</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
            <div className="space-y-1 sm:col-span-2">
              <Label className="text-xs font-semibold text-slate-500">Client Name *</Label>
              <Input data-testid="inv-client-name" value={form.client_name} onChange={(e) => handleChange('client_name', e.target.value)}
                placeholder="Enter client / company name" className="h-10 rounded-xl text-sm" />
            </div>
            <div className="space-y-1 sm:col-span-2">
              <Label className="text-xs font-semibold text-slate-500">Address</Label>
              <Input data-testid="inv-client-address" value={form.client_address} onChange={(e) => handleChange('client_address', e.target.value)}
                placeholder="Full address" className="h-10 rounded-xl text-sm" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-semibold text-slate-500">GSTIN</Label>
              <Input data-testid="inv-client-gstin" value={form.client_gstin} onChange={(e) => handleChange('client_gstin', e.target.value.toUpperCase())}
                placeholder="e.g. 29AAICS1847A1ZF" className="h-10 rounded-xl text-sm uppercase" maxLength={15} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-semibold text-slate-500">State</Label>
              <Input data-testid="inv-client-state" value={form.client_state} onChange={(e) => handleChange('client_state', e.target.value)}
                placeholder="e.g. Karnataka" className="h-10 rounded-xl text-sm" />
            </div>
          </div>

          {/* Invoice Details Section */}
          <h3 className="text-sm font-bold text-slate-700 mb-3 uppercase tracking-wide border-t border-slate-100 pt-4">Invoice Details</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs font-semibold text-slate-500">Invoice Number *</Label>
              <Input data-testid="inv-number" value={form.invoice_number} onChange={(e) => handleChange('invoice_number', e.target.value)}
                placeholder="e.g. MJ/SS25010001" className="h-10 rounded-xl text-sm" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-semibold text-slate-500">Invoice Date</Label>
              <Input data-testid="inv-date" type="date" value={form.date} onChange={(e) => handleChange('date', e.target.value)}
                className="h-10 rounded-xl text-sm" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-semibold text-slate-500">Product</Label>
              <Select value={form.product} onValueChange={(v) => handleChange('product', v)}>
                <SelectTrigger className="h-10 rounded-xl text-sm" data-testid="inv-product"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PRODUCTS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-semibold text-slate-500">PO Number</Label>
              <Input data-testid="inv-po" value={form.po_number} onChange={(e) => handleChange('po_number', e.target.value)}
                placeholder="Optional" className="h-10 rounded-xl text-sm" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-semibold text-slate-500">Quantity (Kgs) *</Label>
              <Input data-testid="inv-kgs" type="number" value={form.kgs} onChange={(e) => handleChange('kgs', e.target.value)}
                placeholder="0" className="h-10 rounded-xl text-sm" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-semibold text-slate-500">Rate (per Kg) *</Label>
              <Input data-testid="inv-rate" type="number" step="0.01" value={form.rate} onChange={(e) => handleChange('rate', e.target.value)}
                placeholder="0.00" className="h-10 rounded-xl text-sm" />
            </div>
          </div>

          {/* Transport & Options */}
          <h3 className="text-sm font-bold text-slate-700 mb-3 uppercase tracking-wide border-t border-slate-100 pt-4 mt-4">Transport & Options</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="space-y-1">
              <Label className="text-xs font-semibold text-slate-500">Vehicle Number</Label>
              <Input data-testid="inv-vehicle" value={form.vehicle_number} onChange={(e) => handleChange('vehicle_number', e.target.value)}
                placeholder="Optional" className="h-10 rounded-xl text-sm" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-semibold text-slate-500">Transport Mode</Label>
              <Input data-testid="inv-transport-mode" value={form.transport_mode} onChange={(e) => handleChange('transport_mode', e.target.value)}
                placeholder="e.g. By Lorry" className="h-10 rounded-xl text-sm" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-semibold text-slate-500">Reverse Charge</Label>
              <Select value={form.reverse_charge} onValueChange={(v) => handleChange('reverse_charge', v)}>
                <SelectTrigger className="h-10 rounded-xl text-sm" data-testid="inv-reverse-charge"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="N">No</SelectItem>
                  <SelectItem value="Y">Yes</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Stamp toggle */}
          <div className="mt-4 flex items-center justify-between">
            <label className="flex items-center gap-2 cursor-pointer" data-testid="inv-stamp-toggle">
              <input type="checkbox" checked={form.include_stamp} onChange={(e) => handleChange('include_stamp', e.target.checked)}
                className="w-4 h-4 rounded border-slate-300 text-amber-500 focus:ring-amber-500" />
              <Stamp className="w-4 h-4 text-slate-400" />
              <span className="text-sm text-slate-600">Include Stamp & Signature</span>
            </label>
          </div>

          {/* Generate Button */}
          <Button
            data-testid="inv-generate-btn"
            onClick={handleGenerate}
            disabled={generating}
            className="w-full mt-5 h-11 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white rounded-xl font-semibold shadow-lg shadow-amber-500/25"
          >
            {generating ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Generating...</> : <><FileText className="w-4 h-4 mr-2" /> Generate Invoice</>}
          </Button>
        </Card>

        {/* Right — Summary & Actions */}
        <div className="space-y-4">
          {/* Live Calculation */}
          <Card className="p-5 border-0 shadow-sm bg-white rounded-2xl">
            <h3 className="text-sm font-bold text-slate-700 mb-3 uppercase tracking-wide">Amount Summary</h3>
            <div className="space-y-2.5">
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Base Amount</span>
                <span className="font-semibold text-slate-800">{baseAmount > 0 ? `₹${baseAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : '—'}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">GST (5%)</span>
                <span className="font-semibold text-slate-800">{gstAmount > 0 ? `₹${gstAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : '—'}</span>
              </div>
              <div className="border-t border-slate-100 pt-2 flex justify-between">
                <span className="text-sm font-bold text-slate-700">Total Amount</span>
                <span className="text-lg font-extrabold text-amber-600">{totalAmount > 0 ? `₹${totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : '—'}</span>
              </div>
              {form.reverse_charge === 'Y' && (
                <div className="text-xs text-red-500 font-medium mt-1">Reverse Charge: Yes</div>
              )}
            </div>
          </Card>

          {/* Client Preview */}
          {form.client_name && (
            <Card className="p-5 border-0 shadow-sm bg-slate-50 rounded-2xl">
              <h3 className="text-sm font-bold text-slate-700 mb-2 uppercase tracking-wide">Client Preview</h3>
              <p className="text-sm font-semibold text-slate-900">{form.client_name}</p>
              {form.client_address && <p className="text-xs text-slate-500 mt-0.5">{form.client_address}</p>}
              {form.client_gstin && <p className="text-xs text-slate-500 mt-0.5 font-mono">GSTIN: {form.client_gstin}</p>}
              {form.client_state && <p className="text-xs text-slate-500 mt-0.5">{form.client_state}</p>}
            </Card>
          )}

          {/* Generated Invoice Actions */}
          {generatedInvoice && (
            <Card className="p-5 border-0 shadow-sm bg-emerald-50 rounded-2xl border-l-4 border-l-emerald-500">
              <h3 className="text-sm font-bold text-emerald-800 mb-2">Invoice Generated</h3>
              <div className="space-y-1 mb-4">
                <p className="text-sm text-emerald-700"><span className="font-semibold">Invoice No:</span> {generatedInvoice.invoice_number}</p>
                <p className="text-sm text-emerald-700"><span className="font-semibold">Amount:</span> ₹{generatedInvoice.invoice_amount?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>
                <p className="text-sm text-emerald-700"><span className="font-semibold">Reverse Charge:</span> {generatedInvoice.reverse_charge}</p>
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={handleDownload} className="gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl" data-testid="inv-download">
                  <Download className="w-3.5 h-3.5" /> Download PDF
                </Button>
                <Button size="sm" variant="outline" onClick={handlePrint} className="gap-1.5 rounded-xl border-emerald-300 text-emerald-700 hover:bg-emerald-100" data-testid="inv-print">
                  <Printer className="w-3.5 h-3.5" /> Print
                </Button>
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
